"use strict";
/**
 * Canonical venue vocabulary, shared by client and server.
 *
 * The same venue used to be spelled three different ways — `"pm-us"` in client
 * state and the PnL query params, `"polymarketUs"` in the health snapshot, and
 * `"polymarket_us"` in the DB — and the conversion between them was written out
 * by hand in three separate places. Every spelling now derives from `VenueId`
 * and every conversion goes through a helper below.
 *
 * Adding a venue means adding it to `VENUE_IDS` (plus the maps, which the
 * compiler will demand). Whether it also becomes *selectable* or *health-probed*
 * is a separate, later decision — see `DATA_SOURCES` and `VENUE_STATUS_KEYS`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.VENUE_STATUS_LABELS = exports.VENUE_STATUS_KEYS = exports.DATA_SOURCES = exports.VENUE_IDS = void 0;
exports.isVenueId = isVenueId;
exports.isDataSource = isDataSource;
exports.toDbVenue = toDbVenue;
exports.fromDbVenue = fromDbVenue;
exports.venueLabel = venueLabel;
exports.parseVenueParam = parseVenueParam;
// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------
/**
 * Every venue the system knows about, in any layer.
 *
 * - `"kalshi"`     – Kalshi REST/WS
 * - `"polymarket"` – original Polymarket via CLOB; functional but frozen
 *                    (REVIEW_FOR_DELETION.md §3)
 * - `"pm-us"`      – Polymarket US
 * - `"novig"`      – Novig sports exchange (ai/NOVIG_VENUE_PLAN.md)
 */
exports.VENUE_IDS = ["kalshi", "polymarket", "pm-us", "novig"];
/**
 * Venues the operator can select in the UI today.
 *
 * Deliberately narrower than {@link VenueId}: the DB, config and reporting
 * layers can already speak `"novig"` while the venue is still being built,
 * without a half-finished venue showing up in the picker. Phase 3 of
 * `ai/NOVIG_VENUE_PLAN.md` adds it here, and the compile errors that produces
 * are precisely the sites that need to learn about a third venue.
 */
exports.DATA_SOURCES = ["kalshi", "polymarket", "pm-us", "novig"];
/** Compile-time proof that every selectable venue is a known venue. */
const _selectableAreKnown = exports.DATA_SOURCES;
void _selectableAreKnown;
function isVenueId(value) {
    return exports.VENUE_IDS.includes(value);
}
function isDataSource(value) {
    return exports.DATA_SOURCES.includes(value);
}
const DB_VENUE = {
    kalshi: "kalshi",
    // Legacy Polymarket has no order-log writer, so this row is never written
    // today; the mapping exists so the conversion stays total over VenueId.
    polymarket: "polymarket",
    "pm-us": "polymarket_us",
    novig: "novig",
};
const VENUE_FROM_DB = {
    kalshi: "kalshi",
    polymarket: "polymarket",
    polymarket_us: "pm-us",
    novig: "novig",
};
/** Canonical id → the spelling stored in the DB. */
function toDbVenue(venue) {
    return DB_VENUE[venue];
}
/**
 * DB spelling → canonical id. Returns null for anything unrecognised, since
 * the column is `text` and may hold rows written by an older build.
 */
function fromDbVenue(venue) {
    return VENUE_FROM_DB[venue] ?? null;
}
// ---------------------------------------------------------------------------
// Health snapshot keys
// ---------------------------------------------------------------------------
/**
 * Venues carried in the `/api/venue-status` snapshot, in render order.
 *
 * A camelCase spelling because these are object keys on the wire. This is the
 * set of *probed* venues, not of known ones: legacy Polymarket has no probe,
 * and Novig is probed only when configured (unprobed, it stays optimistically
 * healthy, so its dot never shows). Adding an entry here is the whole
 * change — the snapshot, the reset helper and the label map are all keyed off
 * it, and the label map will fail to compile until a label is supplied.
 */
exports.VENUE_STATUS_KEYS = ["kalshi", "polymarketUs", "novig"];
/** Display labels for the health indicator. */
exports.VENUE_STATUS_LABELS = {
    kalshi: "Kalshi",
    polymarketUs: "Polymarket US",
    novig: "Novig",
};
// ---------------------------------------------------------------------------
// Display + request parsing
// ---------------------------------------------------------------------------
const VENUE_LABELS = {
    kalshi: "Kalshi",
    polymarket: "Polymarket",
    "pm-us": "Polymarket US",
    novig: "Novig",
};
/** Human-readable venue name. */
function venueLabel(venue) {
    return VENUE_LABELS[venue];
}
/**
 * Validate a venue query parameter against the subset a route actually serves.
 *
 * Routes pass their own `allowed` list rather than accepting every `VenueId`,
 * so a venue that exists but has no implementation on that route is rejected
 * rather than reaching a lookup that returns nothing.
 */
function parseVenueParam(value, allowed) {
    return allowed.includes(value) ? value : null;
}
